import { Alert, Box, Button, Chip, CircularProgress, List, ListItemButton, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { STATUS_COLORS, STATUS_LABELS } from './types';
import { useTemplateVersions } from './useTemplateVersions';

// Clamped rather than nowrap, so a long first line never widens the table.
const ONE_LINE = { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' } as const;

const firstLine = (text: string | null | undefined) => (text ?? '').split('\n')[0].trim();

/** Every version of the template's family, newest first; each opens in the editor. Fetched when it mounts. */
const VersionsPanel = ({ uuid }: { uuid: string }) => {
  const { versions, loading, error, retry } = useTemplateVersions(uuid);

  if (loading)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, color: 'text.secondary' }}>
        <CircularProgress size={16} aria-label="Loading versions" />
        <Typography variant="body2">Loading versions</Typography>
      </Box>
    );
  if (error)
    return (
      <Alert
        severity="error"
        sx={{ my: 1 }}
        action={
          <Button color="inherit" size="small" onClick={retry}>
            Retry
          </Button>
        }
      >
        Could not load the versions: {error}
      </Alert>
    );

  return (
    <List dense disablePadding aria-label="Versions" sx={{ my: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
      {(versions ?? []).map((version, index) => {
        const description = firstLine(version.description);
        return (
          <ListItemButton key={version.uuid} component={RouterLink} to={`/pay-policy-templates/${version.uuid}`} divider={index < (versions ?? []).length - 1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 32 }}>
              v{version.version}
            </Typography>
            <Chip size="small" label={STATUS_LABELS[version.status] ?? version.status} color={STATUS_COLORS[version.status] ?? 'default'} />
            {index === 0 && <Chip size="small" variant="outlined" color="primary" label="newest" />}
            <Typography variant="caption" color="text.secondary">
              Created {new Date(version.createdAt).toLocaleDateString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {version.plugins.length === 1 ? '1 plugin' : `${version.plugins.length} plugins`}
            </Typography>
            {description && (
              <Typography variant="caption" color="text.secondary" sx={{ ...ONE_LINE, flex: '1 1 160px', minWidth: 0 }}>
                {description}
              </Typography>
            )}
          </ListItemButton>
        );
      })}
    </List>
  );
};

export default VersionsPanel;
